import makeActionCreator from 'src/lib/makeActionCreator';
import { State } from 'src/redux/reducers/RootReducer';
import {
  deleteSubscriptionData,
  getSubscriptionData,
  postSubscriptionData,
  putSubscriptionData,
} from 'src/apis/SubscriptionAPI';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { setAlert } from 'src/redux/actions/alertActions';
import { getContactAction } from 'src/redux/actions/contactActions';
import { setError } from 'src/redux/actions/errorsActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { resetSelections } from 'src/redux/actions/selectionActions';
import { setFormStatus } from 'src/redux/actions/formActions';
import { closeModal } from 'src/redux/actions/modalActions';
import { closeDialog } from 'src/redux/actions/dialogActions';
import { SUBSCRIPTION_STATE, SubscriptionPayload } from 'src/models/SubscriptionModel';
import { MODALS } from 'src/models/ModalModel';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { DIALOGS } from 'src/models/DialogModel';

export const UPDATE_SUBSCRIPTION = 'UPDATE_SUBSCRIPTION';
export const RESET_SUBSCRIPTION = 'RESET_SUBSCRIPTION';

export const updateSubscriptionAction = makeActionCreator(UPDATE_SUBSCRIPTION);
export const resetSubscriptionAction = makeActionCreator(RESET_SUBSCRIPTION);

export const getSubscriptionAction: (id: number) => ThunkedAction<State> =
  (id: number) => async (dispatch: any, getState: any) => {
    if (!id) {
      return;
    }
    try {
      dispatch(setFormStatus({ [FORM.get_subscription]: FORM_STATUS.loading }));
      const organisation = getState().currentOrganisation.id;
      const response = await getSubscriptionData(id, organisation);
      if (!response.success) {
        dispatch(setFormStatus({ [FORM.get_subscription]: FORM_STATUS.error }));
        return;
      }
      const { data } = response;
      dispatch(updateSubscriptionAction({ id: data.id, subscription: data }));
      if (data.contact_id) {
        await dispatch(getContactAction(data.contact_id, FORM.get_subscription));
      }
      dispatch(setFormStatus({ [FORM.get_subscription]: FORM_STATUS.success }));
    } catch (e) { /* Log the error here */
      dispatch(setFormStatus({ [FORM.get_subscription]: FORM_STATUS.error }));
    }
  };

export const postSubscriptionAction:
(payload: SubscriptionPayload, close: boolean) => ThunkedAction<State> =
  (payload: SubscriptionPayload, close) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.add_subscription]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await postSubscriptionData(organisation, payload);
      const { data, success } = response;
      if (success) {
        dispatch(setFormStatus({ [FORM.add_subscription]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.subscription_saved',
        }));
        dispatch(updateSubscriptionAction({
          id: data.id,
          subscription: data,
        }));
        if (close) {
          dispatch(closeModal({ modal: MODALS.addSubscription }));
        }
        dispatch(getEntitiesAction(ENTITIES.subscriptions));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.add_subscription]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.add_subscription]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: Object.values(response.errors.fields)[0],
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.subscription_saved_error',
        }));
        dispatch(setFormStatus({ [FORM.add_subscription]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setFormStatus({ [FORM.add_subscription]: FORM_STATUS.error }));
    }
  };

export const putSubscriptionAction:
(id: number, payload: SubscriptionPayload) => ThunkedAction<State> =
  (id: number, payload: SubscriptionPayload) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await putSubscriptionData(id, organisation, payload);
      const { data, success } = response;
      if (success) {
        dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.subscription_updated',
        }));
        dispatch(updateSubscriptionAction({
          id,
          subscription: data,
        }));
        dispatch(getEntitiesAction(ENTITIES.subscriptions));
        dispatch(closeModal({ modal: MODALS.editSubscription }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.put_subscription]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: Object.values(response.errors.fields)[0],
        }));
      } else {
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: 'messages.subscription_updated_error',
        }));
        dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.error }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.subscription_updated_error',
      }));
      dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.error }));
    }
  };

export const deleteSubscriptionAction:
(id: number) => ThunkedAction<State> =
  (id: number) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.delete_subscription]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await deleteSubscriptionData(id, organisation);
      if (response.success) {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: 'messages.subscription_deleted',
        }));
        dispatch(resetSubscriptionAction({ subscriptionId: id }));
        dispatch(getEntitiesAction(ENTITIES.subscriptions));
        dispatch(closeDialog({ dialog: DIALOGS.deleteSubscription }));
        dispatch(setFormStatus({ [FORM.delete_subscription]: FORM_STATUS.success }));
        dispatch(resetSelections({ entity: ENTITIES.subscriptions }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.delete_subscription]: response.message }));
        dispatch(setFormStatus({ [FORM.delete_subscription]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: response.message,
        }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.subscription_deleted_error',
      }));
      dispatch(setFormStatus({ [FORM.delete_subscription]: FORM_STATUS.error }));
    }
  };

export const stopResumeSubscriptionAction:
(id: number, active: boolean) => ThunkedAction<State> =
  (id: number, active: boolean) => async (dispatch: any, getState: any) => {
    try {
      dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.processing }));
      const organisation = getState().currentOrganisation.id;
      const response = await putSubscriptionData(id, organisation, {
        state: active ? SUBSCRIPTION_STATE.active : SUBSCRIPTION_STATE.stopped,
      });
      if (response.success) {
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          code: active ? 'messages.subscription_resumed' : 'messages.subscription_stopped',
        }));
        dispatch(getEntitiesAction(ENTITIES.subscriptions));
        dispatch(closeDialog({ dialog: DIALOGS.stopResumeSubscription }));
        dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.success }));
        dispatch(resetSelections({ entity: ENTITIES.subscriptions }));
      } else if (response.status === 400) {
        dispatch(setError({ [FORM.put_subscription]: response.message }));
        dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.error }));
        dispatch(setAlert({
          type: ALERT_TYPE.error,
          code: response.message,
        }));
      }
    } catch (e) { /* Log the error here */
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: 'messages.subscription_updated_error',
      }));
      dispatch(setFormStatus({ [FORM.put_subscription]: FORM_STATUS.error }));
    }
  };
